import { Icon } from "./Common.jsx";
import iconSet from "../lib/icons.json" with { type: "json" };

// Dev-only review surface for the UI icon sprite (docs/ui-icons.md) — reachable at
// ?icons=1, linked from nowhere. See App.jsx's hook right before its own return().
//
// Three sections, in the order a reviewer would want them: every icon at production size
// (24px, default slate) grouped the way icons.json groups them; one row per palette hue so
// a mistint stands out immediately; then the full set again at 18px, the smallest size any
// of these ship at, to catch anything that fills solid or loses detail at the small end
// (see docs/ui-icons.md's "Small-size rule").
const HUES = ["slate", "gold", "teal", "pink", "indigo", "amber", "rose", "knockout"];

function groupIcons() {
  const groups = new Map();
  for (const icon of iconSet.icons) {
    if (!groups.has(icon.group)) groups.set(icon.group, []);
    groups.get(icon.group).push(icon);
  }
  return groups;
}

function IconTile({ icon, size, hue }) {
  return (
    <div className="flex flex-col items-center gap-1 w-20" title={icon.id}>
      <div className={hue === "knockout" ? "bg-slate-700 rounded-lg p-2" : "p-2"}>
        <Icon id={icon.id} size={size} hue={hue} />
      </div>
      <div className="text-xs text-slate-500 text-center leading-tight break-all">{icon.id}</div>
    </div>
  );
}

export function IconGallery() {
  const groups = groupIcons();

  return (
    <div className="max-w-4xl mx-auto p-5 text-slate-800">
      <h1 className="text-xl font-bold mb-1">Little Bankers UI icons</h1>
      <p className="text-sm text-slate-500 mb-6">
        {iconSet.icons.length} icons · dev-only review page · remove <code>?icons=1</code> to exit.
      </p>

      <h2 className="text-base font-semibold mt-8 mb-3">By group, 24px, slate</h2>
      {[...groups.entries()].map(([group, icons]) => (
        <div key={group} className="mb-6">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{group}</div>
          <div className="flex flex-wrap gap-3">
            {icons.map((icon) => (
              <IconTile key={icon.id} icon={icon} size={24} />
            ))}
          </div>
        </div>
      ))}

      <h2 className="text-base font-semibold mt-10 mb-3">Palette, one row per hue</h2>
      {HUES.map((hue) => (
        <div key={hue} className="mb-4">
          <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">{hue}</div>
          <div className="flex flex-wrap gap-3">
            {iconSet.icons.map((icon) => (
              <IconTile key={icon.id} icon={icon} size={24} hue={hue} />
            ))}
          </div>
        </div>
      ))}

      <h2 className="text-base font-semibold mt-10 mb-3">Full set at 18px (small-size stress)</h2>
      <div className="flex flex-wrap gap-3">
        {iconSet.icons.map((icon) => (
          <IconTile key={icon.id} icon={icon} size={18} />
        ))}
      </div>
    </div>
  );
}
